'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import Image from 'next/image'

import Autoplay from 'embla-carousel-autoplay'
import { ArrowRightIcon } from 'lucide-react'

import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel'
import type { CarouselApi } from '@/components/ui/carousel'
import { resolveImageSrc } from '@/lib/image-src'
import { cn } from '@/lib/utils'
import type { Section } from '@/types/website'
import { SITE_CONTAINER } from '@/components/layout/container'
import { PillButton } from '@/components/blocks/pill-button'
import {
  HeroVideo,
  HeroVideoOverlay,
  hintHeroVideo,
  toHeroVideo,
  usePageVisible,
  useVideoAllowed
} from '@/components/sections/hero-video'

/** Видео слайдын талбарууд (`mediaType`, `hlsUrl`, …)-ыг `toHeroVideo` уншина */
type Slide = {
  image?: string | null
  eyebrow?: string | null
  heading?: string | null
  subheading?: string | null
  cta_label?: string | null
  cta_url?: string | null
} & Record<string, unknown>

/**
 * Бүтэн дэлгэцийн үндсэн слайдер.
 *
 * Слайдууд нь блокийн `items`-ээс ирнэ. Нэг ч слайд байхгүй бол блокийн
 * үндсэн гарчгийг ганц слайд болгон харуулна.
 *
 * Видео слайд (`mediaType: 'video'`, hero-video.tsx) тоглож байх хооронд
 * слайдын таймер ЗОГСОНО — видео дуусмагц дараагийнх руу шилжинэ. Видео
 * тоглохгүй бол (хөтөч хориглосон, алдаа, хөдөлгөөн багасгах тохиргоо)
 * постер зураг нь энгийн слайд шиг `interval`-аар солигдоно.
 */
export const SectionHero = ({ section }: { section: Section }) => {
  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)
  const [failed, setFailed] = useState<Record<number, boolean>>({})
  const [inView, setInView] = useState(true)
  const sectionRef = useRef<HTMLElement>(null)
  const holdRef = useRef(false)

  const slides = (section.items as Slide[]).length
    ? (section.items as Slide[])
    : [
        {
          eyebrow: section.eyebrow,
          heading: section.heading,
          subheading: section.subheading,
          cta_label: section.cta.label,
          cta_url: section.cta.url
        }
      ]

  const interval = Number(section.settings.interval ?? 6000)
  const autoplay = section.settings.autoplay !== false

  const videos = slides.map(slide => toHeroVideo(slide))
  const hasVideo = videos.some(Boolean)
  const videoAllowed = useVideoAllowed()
  const pageVisible = usePageVisible()

  // Эхний слайд видео бол медиа хост руу холбогдож, манифестыг нь <head>-ээс татуулна
  hintHeroVideo(videos[0])

  // Видео дуусмагц шилжих боломжтой эсэх — үгүй бол видео давтагдана
  const canAdvance = autoplay && slides.length > 1
  const currentVideo = videoAllowed && !failed[current] ? videos[current] : null
  const hold = Boolean(currentVideo?.advanceOnEnd && canAdvance)
  const nextIndex = (current + 1) % slides.length

  useEffect(() => {
    if (!api) return

    const onSelect = () => setCurrent(api.selectedScrollSnap())

    api.on('select', onSelect)
    onSelect()

    return () => {
      api.off('select', onSelect)
    }
  }, [api])

  /*
   * ! Видео тоглож байхад таймерыг ЗОГСООНО. Видеоноос гармагц (hold true →
   * ! false) `play()`-ээр сэргээнэ — зогсоосон нь бид өөрсдөө. Гэхдээ KEYBOARD фокус
   * ! (`:focus-visible`) слайдер ДОТОР бол (видео слайдын товчноос Tab-аар дараагийнх
   * ! руу) сэргээхгүй: фокус гармагц plugin өөрөө (`focusout`) үргэлжлүүлнэ. Хулганаар
   * ! дарсан холбоос фокустай үлдсэн ч энэ нь биш — эс бөгөөс слайдер зогссон чигээр.
   *
   * ! Энгийн слайд солигдоход зөвхөн `reset()` — таймер ИДЭВХТЭЙ бол л шинээр
   * ! эхэлнэ (embla-carousel-autoplay 8.6.0). Цэг дээр дарахад шинэ слайд бүтэн
   * ! хугацаагаа авна (plugin өмнөх таймераа үргэлжлүүлж хугацаанаасаа өмнө
   * ! солигддог байв). Харин Tab-аар слайд дотор фокуслахад plugin-ий зогсоолт
   * ! (`stopOnFocusIn`) хэвээр үлдэнэ — `play()` дуудвал слайд фокустай
   * ! холбоосоос холдож, тэр нь дэлгэцээс гадна үлддэг байв.
   *
   * ! Ганц слайдтай үед plugin эхлэлгүй (`delay` тодорхойгүй) үлддэг тул
   * ! `play()` дуудвал унана — тэр үед огт хөндөхгүй.
   */
  useEffect(() => {
    // Өмнөх `hold` — `holdRef`-ийг зөвхөн энэ effect бичдэг
    const wasHeld = holdRef.current

    holdRef.current = hold

    if (!api || slides.length <= 1) return

    const plugin = api.plugins().autoplay

    if (!plugin) return

    if (hold) {
      plugin.stop()
    } else if (wasHeld) {
      const focused = document.activeElement

      if (!(focused && api.containerNode().contains(focused) && focused.matches(':focus-visible'))) plugin.play()
    } else {
      plugin.reset()
    }
  }, [api, current, hold, slides.length])

  /*
   * ! Plugin өөрөө ч таймераа дахин асаадаг: товшсон/чирсний дараа (pointerUp —
   * ! слайдер дээрээс эхэлсэн босоо гүйлгээ ч), фокус гарахад (focusout). Видео
   * ! тоглож байхад тэдгээрийг шууд унтраана.
   *
   * ! МИКРОДААЛГАВРААР. `autoplay:timerset` нь plugin-ий `startAutoplay` дотор
   * ! `autoplayActive = true` болохоос ӨМНӨ гардаг — тэр мөчид `stop()`
   * ! (`if (autoplayActive)`) юу ч хийхгүй, таймер үлдэж видео дундуур слайд
   * ! солигддог байв (embla-carousel-autoplay 8.6.0).
   */
  useEffect(() => {
    if (!api) return

    const guard = () => {
      if (!holdRef.current) return

      queueMicrotask(() => {
        if (holdRef.current) api.plugins().autoplay?.stop()
      })
    }

    api.on('autoplay:timerset', guard)

    return () => {
      api.off('autoplay:timerset', guard)
    }
  }, [api])

  /*
   * ! Слайдер дэлгэцэд 20%-иас бага харагдвал видеог түр зогсооно — үл үзэгдэн
   * ! дуусаж шилжихгүй. `isIntersecting` дангаараа 1px харагдсан ч true тул
   * ! харьцааг шалгана; 0 босго нь бүрэн гарах/орохыг ч барина.
   */
  useEffect(() => {
    const node = sectionRef.current

    if (!node || !hasVideo || typeof IntersectionObserver === 'undefined') return

    // ! Нэг дуудалтад хэд хэдэн огтлолцол ирж болно — ХАМГИЙН СҮҮЛИЙНХ нь одоогийн байдал
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[entries.length - 1]

        if (entry) setInView(entry.isIntersecting && entry.intersectionRatio >= 0.2)
      },
      { threshold: [0, 0.2] }
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [hasVideo])

  const advance = useCallback(() => {
    if (!api) return

    if (api.canScrollNext()) {
      api.scrollNext()
    } else {
      api.scrollTo(0)
    }
  }, [api])

  return (
    <section ref={sectionRef} className='relative'>
      <Carousel
        setApi={setApi}
        opts={{ loop: section.settings.loop !== false, align: 'start' }}
        plugins={autoplay ? [Autoplay({ delay: interval, stopOnInteraction: false })] : []}
      >
        <CarouselContent className='ml-0'>
          {slides.map((slide, i) => {
            const video = videos[i]
            const image = resolveImageSrc(video ? video.poster : slide.image)

            // Зураг эсвэл видеотой слайд дээр бичиг цагаан байна
            const media = Boolean(image) || video != null

            return (
              <CarouselItem key={i} className='relative h-svh p-0'>
                {image ? (
                  <Image src={image} alt={slide.heading ?? ''} fill priority={i === 0} className='object-cover' />
                ) : video ? (
                  <div className='absolute inset-0 bg-black' />
                ) : (
                  <div className='from-muted via-background to-muted absolute inset-0 bg-gradient-to-br' />
                )}

                {video && !failed[i] && (
                  <HeroVideo
                    key={video.hls ?? video.mp4}
                    source={video}
                    enabled={videoAllowed}
                    selected={i === current}
                    preload={i === nextIndex}
                    paused={!inView || !pageVisible}
                    loop={!canAdvance || !video.advanceOnEnd}
                    onEnded={() => {
                      if (i === current && canAdvance && video.advanceOnEnd) advance()
                    }}
                    onFail={() => setFailed(prev => (prev[i] ? prev : { ...prev, [i]: true }))}
                  />
                )}

                {video ? (
                  <HeroVideoOverlay gradient={video.overlay} posterOnly={!videoAllowed || Boolean(failed[i])} />
                ) : (
                  image && <div className='absolute inset-0 bg-black/45' />
                )}

                <div className={cn(SITE_CONTAINER, 'relative flex h-full flex-col justify-center gap-6')}>
                  {slide.eyebrow && (
                    <span
                      className={cn(
                        'text-xl font-medium tracking-widest uppercase',
                        media ? 'text-white/80' : 'text-muted-foreground'
                      )}
                    >
                      {slide.eyebrow}
                    </span>
                  )}

                  {slide.heading && (
                    <h1
                      className={cn(
                        'max-w-4xl text-4xl font-semibold tracking-tight text-balance md:text-6xl lg:text-7xl',
                        media && 'text-white'
                      )}
                    >
                      {slide.heading}
                    </h1>
                  )}

                  {slide.subheading && (
                    <p
                      className={cn(
                        'max-w-2xl text-xl text-pretty md:text-xl',
                        media ? 'text-white/90' : 'text-muted-foreground'
                      )}
                    >
                      {slide.subheading}
                    </p>
                  )}

                  {slide.cta_url && slide.cta_label && (
                    <PillButton href={slide.cta_url} className='group mt-2 w-fit'>
                      {slide.cta_label}
                      <ArrowRightIcon className='size-4 transition-transform group-hover:translate-x-1' />
                    </PillButton>
                  )}
                </div>
              </CarouselItem>
            )
          })}
        </CarouselContent>

        {slides.length > 1 && (
          <>
            <div className='absolute bottom-10 left-1/2 flex -translate-x-1/2 items-center gap-2'>
              {slides.map((_, i) => (
                <button
                  key={i}
                  type='button'
                  aria-label={`Слайд ${i + 1}`}
                  onClick={() => api?.scrollTo(i)}
                  className={cn(
                    'h-1.5 rounded-full transition-all',
                    i === current ? 'bg-foreground w-8' : 'bg-foreground/30 w-4'
                  )}
                />
              ))}
            </div>
          </>
        )}
      </Carousel>
    </section>
  )
}
